/**
 * UnifiedRightPanel — Pannello destro unificato CRM + Agent AI
 *
 * Con Lead/Cliente associato: sezioni CRM (note, Task commerciale, offerte, progetti, contatti)
 *   con suggerimenti AI distribuiti inline nelle sezioni pertinenti.
 * Senza Lead/Cliente: solo suggerimenti AI.
 *
 * Orchestratore: gestisce stato e handlers, delega il rendering ai sotto-componenti.
 * Le parti comuni con il pannello Instagram/Messenger (MetaAssistantPanel) stanno in
 * ClientCrmSections, useClientPanelData e AskAiSection, con la "sorgente
 * conversazione" di conversationSource.ts. I suggerimenti AI restano solo WhatsApp.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2, MessageSquare, CheckSquare, Square,
  Trash2, ListTodo,
  Group, Sparkles,
  ChevronRight, ChevronLeft,
  Building2, ExternalLink, Phone, Mail,
  FolderKanban,
  LayoutPanelLeft, RefreshCw, Bot,
} from 'lucide-react';
import { useWhatsAppChatStore } from '@/lib/stores/whatsappChatStore';
import { useWhatsAppAgentStore } from '@/lib/stores/whatsappAgentStore';
import { useAuth } from '@/lib/auth';
import { useApp } from '@/components/AppProvider';
import { supabase } from '@/lib/supabase';
import { WHATSAPP_AUDIO_TRANSCRIPTION_ENABLED } from '@/lib/featureFlags';
import { BulkTaskConfirmModal } from './BulkTaskConfirmModal';
import { CollapsibleSection } from './CollapsibleSection';
import { SuggestionCard } from './SuggestionCard';
import { PROJECT_TYPES, ACTIVITY_TYPES } from './unified-panel-helpers';
import { ClientCrmSections } from './ClientCrmSections';
import { ChatLeadControls } from './ChatLeadControls';
import { SuggestionCrmActions } from './SuggestionCrmActions';
import { pendingCrmActions } from './suggestionCrmActionsLogic';
import { AskAiSection } from './AskAiSection';
import { useClientPanelData } from './useClientPanelData';
import type { ConversationSource } from './conversationSource';
import type { TaskOverride } from './BulkTaskConfirmModal';
import type { AgentRequest } from '@/lib/types/whatsappAgent';
import type { SuggestionCardActions, SuggestionCardState, SuggestionCardSetters } from './SuggestionCard';
import { tip, iconTip } from '@/components/messages/ChatTooltip';
import { useOrgAiEnabled, AI_DISABLED_TOOLTIP } from '@/lib/hooks/useOrgAiEnabled';
import { initialPanelCollapsed, readPanelPref, writePanelPref, safeLocalStorage } from './rightPanelPreference';
import { defaultDueDate, suggestionDescription } from './salesTaskFromSuggestion';
import { createSalesTasks } from './salesTaskService';

// ============ TYPES ============

type ScopeFilter = 'all' | 'in_scope' | 'out_scope' | 'alerts';

// Filtri dei suggerimenti in parole semplici. "in scope" = rientra in quanto
// concordato col cliente (progetto/offerta), "fuori scope" = non rientra,
// "alert" = segnali da guardare subito (urgenza, cliente scontento, da quotare...).
const SCOPE_TAB_LABELS: Record<ScopeFilter, string> = {
  all: 'Tutte',
  in_scope: 'Già previste',
  out_scope: 'Da valutare',
  alerts: 'Attenzione',
};
const SCOPE_TAB_TIPS: Record<ScopeFilter, string> = {
  all: 'Tutte le richieste del cliente',
  in_scope: 'Richieste che rientrano in quanto già concordato con il cliente',
  out_scope: 'Richieste non comprese in quanto concordato: da valutare o preventivare',
  alerts: 'Segnali da guardare subito: urgenze, cliente scontento, richieste da quotare',
};

// ============ MAIN COMPONENT ============

interface UnifiedRightPanelProps {
  chatId: string;
  chatName: string;
  isOpen: boolean;
  onClose: () => void;
  onCreateTask?: (request: AgentRequest) => void;
  /** Apre il pannello unico delle note (nota della chat + nota sulla scheda). */
  onOpenNotes?: () => void;
  /** Numero di note della chat, per il contatore della voce "Note". */
  chatNotesCount?: number;
  /**
   * false quando il pannello e' aperto/chiuso da un pulsante esterno (modalita'
   * compatta): parte espanso e non tocca la preferenza salvata.
   */
  rememberCollapse?: boolean;
}

export function UnifiedRightPanel({
  chatId, chatName, isOpen, onClose, onCreateTask, onOpenNotes, chatNotesCount = 0, rememberCollapse = true,
}: UnifiedRightPanelProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useApp();

  // ======== AGENT STORE ========
  const {
    chatSuggestions, chatSuggestionsLoading, fetchChatSuggestions,
    dismissSuggestion, createTasksFromSuggestions, setPendingDraftText,
    assignments, sendSuggestion, toggleScope, sendClarification,
    groupRequests, ungroupRequest, linkRequestToTask, unlinkRequestFromTask, forwardToTeamChat,
    analysisStatus, chatSummaries, triggerSingleAnalysis,
  } = useWhatsAppAgentStore();
  const { wahaConfig } = useWhatsAppChatStore();

  // Stato analisi e summary dalla store (sostituisce vecchio auto-analyze locale)
  const isAnalyzing = analysisStatus.get(chatId) === 'analyzing';
  const chatSummary = chatSummaries.get(chatId);

  // ======== DERIVED: linked client ========
  const assignment = assignments.find(a => a.chat_jid === chatId);
  const linkedClientId = assignment?.client?.id || null;
  const hasProject = !!assignment?.project_id;
  const projectName = assignment?.project?.name || '';

  // ======== CRM: Lead/Cliente collegato (scheda, offerte, progetti, Task commerciali) ========
  const crm = useClientPanelData({
    clientId: linkedClientId, organizationId: wahaConfig?.organizationId, userId: user?.id, enabled: isOpen, conversationKey: chatId,
  });
  const { client, loadActivities } = crm;

  // Sorgente per "Chiedi all'AI" (conversationSource.ts): la chat WhatsApp con numero, Lead/Cliente, progetto e offerta.
  const askSource: ConversationSource | null = useMemo(() => (
    wahaConfig?.organizationId && wahaConfig?.configId
      ? { kind: 'whatsapp', organizationId: wahaConfig.organizationId, chatJid: chatId, configId: wahaConfig.configId, clientId: linkedClientId, projectId: assignment?.project_id, dealId: assignment?.deal_id }
      : null
  ), [wahaConfig?.organizationId, wahaConfig?.configId, chatId, linkedClientId, assignment?.project_id, assignment?.deal_id]);

  // ======== PANEL STATE ========
  // Sotto i 1440px parte chiuso; poi vale l'ultima scelta dell'utente.
  const [collapsed, setCollapsedState] = useState(() =>
    rememberCollapse
      ? initialPanelCollapsed(typeof window !== 'undefined' ? window.innerWidth : 1920, readPanelPref(safeLocalStorage()))
      : false,
  );
  const setCollapsed = useCallback((value: boolean) => {
    setCollapsedState(value);
    if (rememberCollapse) writePanelPref(safeLocalStorage(), value);
  }, [rememberCollapse]);

  // ======== AGENT STATE ========
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>('all');
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [editResponseId, setEditResponseId] = useState<string | null>(null);
  const [editResponseText, setEditResponseText] = useState('');
  const [clarificationId, setClarificationId] = useState<string | null>(null);
  const [clarificationText, setClarificationText] = useState('');
  const [sendingClarificationId, setSendingClarificationId] = useState<string | null>(null);
  const [forwardingId, setForwardingId] = useState<string | null>(null);
  const [forwardNote, setForwardNote] = useState('');
  const [teamChats, setTeamChats] = useState<{ id: string; name: string | null }[]>([]);
  const [sendingForward, setSendingForward] = useState(false);
  const [linkingTaskId, setLinkingTaskId] = useState<string | null>(null);
  const [projectTasks, setProjectTasks] = useState<{ id: string; title: string; status: string }[]>([]);
  const [taskSearchQuery, setTaskSearchQuery] = useState('');

  // ======== CRM STATE ========
  const [bulkSalesSaving, setBulkSalesSaving] = useState(false);

  // ======== DATA LOADING ========

  useEffect(() => {
    if (isOpen && chatId && wahaConfig?.organizationId) {
      fetchChatSuggestions(chatId, wahaConfig.organizationId);
    }
  }, [isOpen, chatId, wahaConfig?.organizationId, fetchChatSuggestions]);

  useEffect(() => { setSelectedIds(new Set()); }, [chatSuggestions]);

  // Interruttore IA dell'azienda: spento = analisi manuale disattivata.
  const aiEnabled = useOrgAiEnabled();

  // Trigger manuale analisi
  const handleTriggerAnalysis = useCallback(() => {
    if (!wahaConfig?.organizationId || isAnalyzing) return;
    triggerSingleAnalysis(wahaConfig.organizationId, chatId);
  }, [wahaConfig?.organizationId, isAnalyzing, triggerSingleAnalysis, chatId]);

  // ======== FILTERED DATA ========

  const filteredSuggestions = chatSuggestions.filter(s => {
    const hasAlerts = Array.isArray(s.alert_tags) && s.alert_tags.length > 0;
    if (scopeFilter === 'in_scope') return !s.is_out_of_scope && !hasAlerts;
    if (scopeFilter === 'out_scope') return s.is_out_of_scope === true;
    if (scopeFilter === 'alerts') return hasAlerts;
    return true;
  });
  const alertCount = chatSuggestions.filter(s => Array.isArray(s.alert_tags) && s.alert_tags.length > 0).length;
  const inScopeCount = chatSuggestions.filter(s => !s.is_out_of_scope && !(Array.isArray(s.alert_tags) && s.alert_tags.length > 0)).length;
  const outScopeCount = chatSuggestions.filter(s => s.is_out_of_scope === true).length;
  const newCount = chatSuggestions.filter(s => s.status === 'new').length;

  const projectSuggestions = filteredSuggestions.filter(s => PROJECT_TYPES.has(s.request_type));
  const activitySuggestions = filteredSuggestions.filter(s => ACTIVITY_TYPES.has(s.request_type));
  const otherSuggestions = filteredSuggestions.filter(s => !PROJECT_TYPES.has(s.request_type) && !ACTIVITY_TYPES.has(s.request_type));


  // ======== SOURCE PREVIEW HELPER ========

  const getSourceMessagePreview = useCallback((messageIds: string[]) => {
    if (!messageIds || messageIds.length === 0) return null;
    try {
      const { messages } = useWhatsAppChatStore.getState();
      const found = messageIds.map(id => messages.find(m => m.id === id)).filter(Boolean);
      if (found.length === 0) return null;
      const mapped = found.map(msg => {
        const isAudio = msg!.type === 'audio' || msg!.type === 'ptt' || msg!.type === 'voice';
        const content = isAudio && WHATSAPP_AUDIO_TRANSCRIPTION_ENABLED && msg!.transcription ? `🎤 ${msg!.transcription}` : (msg as any).body || msg!.content || '';
        return {
          text: content.substring(0, 200) + (content.length > 200 ? '...' : ''),
          sender: msg!.senderName || (msg as any).from?.split('@')[0] || '',
          // Message.timestamp e' una Date; la card vuole i secondi (prima mostrava "Invalid Date").
          timestamp: msg!.timestamp ? Math.floor(new Date(msg!.timestamp).getTime() / 1000) : 0,
        };
      });
      return { messages: mapped };
    } catch { return null; }
  }, []);

  // ======== SUGGESTION CARD ACTIONS ========

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedIds.size === filteredSuggestions.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filteredSuggestions.map(s => s.id)));
  }, [filteredSuggestions, selectedIds.size]);

  const handleUseResponse = useCallback((text: string) => { setPendingDraftText(text); onClose(); }, [setPendingDraftText, onClose]);

  // AI Composer: inserisce il testo generato nell'input chat
  const handleInsertComposedMessage = useCallback((text: string) => { setPendingDraftText(text); }, [setPendingDraftText]);

  const handleSendNow = useCallback(async (requestId: string) => {
    setSendingId(requestId);
    try { await sendSuggestion(requestId); } finally { setSendingId(null); }
  }, [sendSuggestion]);

  const handleOpenEditResponse = useCallback((requestId: string, currentText: string) => {
    setEditResponseId(requestId); setEditResponseText(currentText); setClarificationId(null);
  }, []);

  const handleSendEditedResponse = useCallback(async (requestId: string, text: string) => {
    if (!text.trim()) return;
    setSendingId(requestId);
    try { await sendSuggestion(requestId, text); setEditResponseId(null); setEditResponseText(''); }
    finally { setSendingId(null); }
  }, [sendSuggestion]);

  const handleOpenClarification = useCallback((requestId: string) => {
    setClarificationId(requestId); setClarificationText(''); setEditResponseId(null);
  }, []);

  const handleSendClarification = useCallback(async (requestId: string, text: string) => {
    if (!text.trim()) return;
    setSendingClarificationId(requestId);
    try { const ok = await sendClarification(requestId, text); if (ok) { setClarificationId(null); setClarificationText(''); } }
    finally { setSendingClarificationId(null); }
  }, [sendClarification]);

  const handleToggleScope = useCallback((requestId: string, currentIsOutOfScope: boolean) => {
    toggleScope(requestId, !currentIsOutOfScope);
  }, [toggleScope]);

  const handleOpenForward = useCallback(async (requestId: string) => {
    setForwardingId(requestId); setForwardNote('');
    if (wahaConfig?.organizationId && teamChats.length === 0) {
      const { data } = await supabase.from('team_chats').select('id, name').eq('organization_id', wahaConfig.organizationId).eq('is_group', true).order('name');
      setTeamChats(data || []);
    }
  }, [wahaConfig?.organizationId, teamChats.length]);

  const handleForwardSend = useCallback(async (teamChatId: string) => {
    if (!forwardingId) return;
    setSendingForward(true);
    await forwardToTeamChat(forwardingId, teamChatId, forwardNote || undefined);
    setSendingForward(false); setForwardingId(null); setForwardNote('');
  }, [forwardingId, forwardNote, forwardToTeamChat]);

  const handleOpenLinkTask = useCallback(async (requestId: string) => {
    setLinkingTaskId(requestId); setTaskSearchQuery('');
    if (assignment?.project_id && projectTasks.length === 0) {
      const { data } = await supabase.from('tasks').select('id, title, status').eq('project_id', assignment.project_id).neq('status', 'done').order('created_at', { ascending: false }).limit(50);
      setProjectTasks(data || []);
    }
  }, [assignment?.project_id, projectTasks.length]);

  const handleLinkTask = useCallback(async (taskId: string, taskTitle: string) => {
    if (!linkingTaskId) return;
    await linkRequestToTask(linkingTaskId, taskId, taskTitle); setLinkingTaskId(null);
  }, [linkingTaskId, linkRequestToTask]);

  const handleUnlinkTask = useCallback(async (requestId: string, taskId: string) => {
    await unlinkRequestFromTask(requestId, taskId);
  }, [unlinkRequestFromTask]);

  const handleDismiss = useCallback((requestId: string) => {
    if (user?.id) dismissSuggestion(requestId, user.id);
  }, [user?.id, dismissSuggestion]);

  const handleGroupSelected = useCallback(async () => {
    if (selectedIds.size < 2) return;
    await groupRequests(Array.from(selectedIds)); setSelectedIds(new Set());
  }, [selectedIds, groupRequests]);

  const handleOpenBulkModal = useCallback(() => {
    if (!assignment?.project_id || selectedIds.size === 0) return;
    setShowBulkModal(true);
  }, [assignment?.project_id, selectedIds.size]);

  const handleBulkConfirm = useCallback(async (overrides: Record<string, TaskOverride>, attachments: Record<string, File[]>) => {
    const projectId = assignment?.project_id;
    if (!projectId) return { created: 0, errors: ['Nessun progetto associato'] };
    const result = await createTasksFromSuggestions(Array.from(selectedIds), projectId, undefined, overrides, attachments);
    setSelectedIds(new Set()); setShowBulkModal(false);
    return result;
  }, [assignment?.project_id, selectedIds, createTasksFromSuggestions]);

  const handleBulkDismiss = useCallback(() => {
    if (!user?.id) return;
    for (const id of selectedIds) dismissSuggestion(id, user.id);
    setSelectedIds(new Set());
  }, [user?.id, selectedIds, dismissSuggestion]);

  // ======== CRM HANDLERS ========

  // Task commerciali in blocco dai suggerimenti selezionati: non serve un progetto,
  // basta il Lead/Cliente collegato alla chat.
  const handleBulkSalesTasks = useCallback(async () => {
    if (!linkedClientId || !user?.id || !wahaConfig?.organizationId || selectedIds.size === 0) return;
    const orgId = wahaConfig.organizationId;
    const dueDate = defaultDueDate(new Date());
    const items = chatSuggestions
      .filter(s => selectedIds.has(s.id))
      .map(request => ({
        request,
        input: { title: request.title, description: suggestionDescription(request), dueDate, clientId: linkedClientId, userId: user.id, organizationId: orgId },
      }));
    setBulkSalesSaving(true);
    try {
      const res = await createSalesTasks(items);
      if (res.doneRequestIds.length > 0) {
        const done = new Set(res.doneRequestIds);
        useWhatsAppAgentStore.setState(st => ({ chatSuggestions: st.chatSuggestions.filter(x => !done.has(x.id)) }));
      }
      if (res.created > 0) toast.success(res.created === 1 ? 'Task commerciale creato' : `${res.created} Task commerciali creati`);
      if (res.errors.length > 0) toast.error(`Non creati: ${res.errors.length}. ${res.errors[0]}`);
      setSelectedIds(new Set());
      loadActivities();
    } finally {
      setBulkSalesSaving(false);
    }
  }, [linkedClientId, user?.id, wahaConfig?.organizationId, selectedIds, chatSuggestions, toast, loadActivities]);

  // ======== SUGGESTION CARD PROPS BUILDER ========

  const cardActions: SuggestionCardActions = useMemo(() => ({
    onToggleSelect: handleToggleSelect,
    onSendNow: handleSendNow,
    onOpenEditResponse: handleOpenEditResponse,
    onSendEditedResponse: handleSendEditedResponse,
    onUseResponse: handleUseResponse,
    onOpenClarification: handleOpenClarification,
    onSendClarification: handleSendClarification,
    onToggleScope: handleToggleScope,
    onDismiss: handleDismiss,
    onCreateTask,
    onOpenLinkTask: handleOpenLinkTask,
    onLinkTask: handleLinkTask,
    onUnlinkTask: handleUnlinkTask,
    onOpenForward: handleOpenForward,
    onForwardSend: handleForwardSend,
    onUngroupRequest: ungroupRequest,
  }), [handleToggleSelect, handleSendNow, handleOpenEditResponse, handleSendEditedResponse, handleUseResponse, handleOpenClarification, handleSendClarification, handleToggleScope, handleDismiss, onCreateTask, handleOpenLinkTask, handleLinkTask, handleUnlinkTask, handleOpenForward, handleForwardSend, ungroupRequest]);

  const cardSetters: SuggestionCardSetters = useMemo(() => ({
    setExpandedId,
    setEditResponseId,
    setEditResponseText,
    setClarificationId,
    setClarificationText,
    setForwardingId,
    setForwardNote,
    setLinkingTaskId,
    setTaskSearchQuery,
  }), []);

  const buildCardState = useCallback((suggestion: AgentRequest): SuggestionCardState => ({
    isSelected: selectedIds.has(suggestion.id),
    isExpanded: expandedId === suggestion.id,
    sendingId,
    editResponseId,
    editResponseText,
    clarificationId,
    clarificationText,
    sendingClarificationId,
    forwardingId,
    forwardNote,
    sendingForward,
    teamChats,
    linkingTaskId,
    projectTasks,
    taskSearchQuery,
    hasProject,
  }), [selectedIds, expandedId, sendingId, editResponseId, editResponseText, clarificationId, clarificationText, sendingClarificationId, forwardingId, forwardNote, sendingForward, teamChats, linkingTaskId, projectTasks, taskSearchQuery, hasProject]);

  // Azioni sul CRM solo per il Lead/Cliente per cui l'assistente le ha proposte
  // (la chat puo' essere stata ricollegata a un altro nel frattempo).
  const renderCard = useCallback((suggestion: AgentRequest, compact = false) => (
    <SuggestionCard
      key={suggestion.id}
      suggestion={suggestion}
      allSuggestions={chatSuggestions}
      actions={cardActions}
      state={buildCardState(suggestion)}
      setters={cardSetters}
      sourcePreview={getSourceMessagePreview(suggestion.source_message_ids)}
      compact={compact}
      crmActions={linkedClientId && client && suggestion.client_id === linkedClientId && pendingCrmActions(suggestion.crm_actions).length > 0
        ? <SuggestionCrmActions suggestion={suggestion} client={client} />
        : null}
    />
  ), [chatSuggestions, cardActions, buildCardState, cardSetters, getSourceMessagePreview, linkedClientId, client]);

  // ======== RENDER ========

  if (!isOpen) return null;

  const allSelected = filteredSuggestions.length > 0 && selectedIds.size === filteredSuggestions.length;

  // --- ANIMATED WRAPPER ---
  return (
    <div
      className={`h-full border-l border-[rgba(55,53,47,0.09)] dark:border-white/10 flex flex-col overflow-hidden bg-white dark:bg-gray-950 transition-[width] duration-200 ease-[cubic-bezier(0.25,0.1,0.25,1)]
        ${collapsed ? 'w-11' : 'w-[380px]'}`}
    >
      {collapsed ? (
        /* --- COLLAPSED --- */
        <div className="w-11 h-full flex flex-col items-center pt-3 gap-2">
          <button
            onClick={() => setCollapsed(false)}
            className="group p-1.5 rounded-md hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/8 transition-colors"
            aria-expanded={false}
            {...iconTip('Apri il pannello assistente')}
          >
            <ChevronLeft className="w-4 h-4 text-[#6F6E69] dark:text-white/60 group-hover:text-[#37352F] dark:group-hover:text-white transition-colors" />
          </button>

          <div className="w-5 h-px bg-[rgba(55,53,47,0.06)] dark:bg-white/6 my-0.5" />

          {/* Client indicator */}
          {linkedClientId && (
            <div className="p-1.5 rounded-md bg-emerald-50 dark:bg-emerald-900/15" {...iconTip('Chat collegata a un Lead/Cliente')}>
              <Building2 className="w-3.5 h-3.5 text-emerald-500 dark:text-emerald-400" />
            </div>
          )}

          {/* AI indicator con badge */}
          <div className="relative p-1.5" {...iconTip(chatSuggestions.length > 0 ? `${chatSuggestions.length} suggerimenti dell'assistente` : 'Assistente AI')}>
            <Sparkles className="w-4 h-4 text-[#6F6E69] dark:text-white/60" />
            {chatSuggestions.length > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-[3px] bg-[#2383E2] text-white text-[10px] font-semibold rounded-full flex items-center justify-center leading-none">
                {chatSuggestions.length}
              </span>
            )}
          </div>

          {/* Analysis status */}
          {isAnalyzing && (
            <div className="p-1.5" {...iconTip('Sto leggendo la chat...')}>
              <Loader2 className="w-3.5 h-3.5 text-[#2383E2] animate-spin" />
            </div>
          )}

          {chatSummary && !isAnalyzing && (
            <div className="p-1.5" {...iconTip('Riepilogo della chat pronto')}>
              <LayoutPanelLeft className="w-3.5 h-3.5 text-purple-400 dark:text-purple-500" />
            </div>
          )}

          {newCount > 0 && (
            <div className="flex flex-col items-center gap-0.5" {...iconTip(`${newCount} nuove richieste del cliente`)}>
              <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
            </div>
          )}
        </div>
      ) : (
        /* --- EXPANDED --- */
        <>
          {/* === HEADER === */}
          <div className="flex items-center gap-2.5 px-3.5 py-2.5 border-b border-[rgba(55,53,47,0.06)] dark:border-white/6 flex-shrink-0">
            <button
              // In compatto il pannello e' aperto da un pulsante esterno e puo' stare sopra
              // la chat: si chiude, invece di restare come striscia sul bordo della chat.
              onClick={() => (rememberCollapse ? setCollapsed(true) : onClose())}
              className="group p-1 rounded-md hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/8 transition-colors"
              aria-expanded={true}
              {...iconTip('Chiudi il pannello assistente')}
            >
              <ChevronRight className="w-3.5 h-3.5 text-[#6F6E69] dark:text-white/60 group-hover:text-[#37352F] dark:group-hover:text-white transition-colors" />
            </button>

            <div className="flex-1 min-w-0">
              {linkedClientId && client ? (
                <>
                  <div className="flex items-center gap-1.5">
                    <Building2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                    <h3 className="text-[13px] font-semibold text-[#37352F] dark:text-white truncate">{client.business_name}</h3>
                    <button onClick={() => navigate(`/clients/${linkedClientId}`)} className="p-0.5 rounded hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/8 text-[#6F6E69] hover:text-[#37352F] dark:hover:text-white transition-colors" {...iconTip('Apri la scheda (chiude i messaggi)')}>
                      <ExternalLink className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    {client.phone && <span className="text-[10px] text-[#6F6E69]"><Phone className="w-2.5 h-2.5 inline mr-0.5" />{client.phone}</span>}
                    {client.email && <span className="text-[10px] text-[#6F6E69]"><Mail className="w-2.5 h-2.5 inline mr-0.5" />{client.email}</span>}
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-[#6F6E69] flex-shrink-0" />
                  <h3 className="text-[13px] font-medium text-[#37352F] dark:text-white truncate">Assistente</h3>
                </div>
              )}
              <p className="text-[10px] text-[#6F6E69] truncate mt-0.5">{chatName}{projectName && ` · ${projectName}`}</p>
            </div>

            {/* Analyze button — ghost style Notion-like */}
            <button onClick={handleTriggerAnalysis} disabled={isAnalyzing || !aiEnabled}
              {...tip(aiEnabled ? "L'assistente rilegge gli ultimi messaggi e aggiorna riepilogo e suggerimenti" : AI_DISABLED_TOOLTIP, 'bottom')}
              className={`flex items-center gap-1.5 px-2 py-1 text-[11px] font-medium rounded-md transition-all disabled:opacity-40 disabled:cursor-not-allowed
                ${isAnalyzing
                  ? 'text-[#2383E2] bg-[#2383E2]/8'
                  : 'text-[#6F6E69] hover:text-[#37352F] dark:hover:text-white hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/8'
                }`}
            >
              {isAnalyzing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              {isAnalyzing ? 'Aggiorno...' : 'Aggiorna i suggerimenti'}
            </button>
          </div>

          {/* === SCROLLABLE CONTENT === */}
          <div className="flex-1 overflow-y-auto">

            {/* === FASE, STATO E ASSEGNATARIO del Lead/Cliente collegato === */}
            {linkedClientId && client && <ChatLeadControls client={client} />}

            {/* === RIEPILOGO AI === */}
            {(chatSummary || isAnalyzing) && (
              <div className="px-3.5 py-3 border-b border-[rgba(55,53,47,0.06)] dark:border-white/6">
                {isAnalyzing ? (
                  <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-[#2383E2]/5 dark:bg-[#2383E2]/10">
                    <Loader2 className="w-4 h-4 text-[#2383E2] animate-spin flex-shrink-0" />
                    <span className="text-xs text-[#2383E2] font-medium">Sto leggendo la chat...</span>
                  </div>
                ) : chatSummary && (
                  <div className="rounded-lg bg-[rgba(55,53,47,0.024)] dark:bg-white/[0.03] px-3 py-2.5">
                    <div className="flex items-center gap-1.5 mb-2">
                      <Sparkles className="w-3.5 h-3.5 text-purple-500 dark:text-purple-400 flex-shrink-0" />
                      <span className="text-[11px] font-semibold text-[#37352F] dark:text-white/90">Riepilogo</span>
                      <span className="ml-auto text-[10px] text-[#6F6E69]">
                        {new Date(chatSummary.generatedAt).toLocaleString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <p className="text-[11px] text-[#37352F] dark:text-white/80 leading-[1.6]">{chatSummary.text}</p>
                    {chatSummary.nextSteps && chatSummary.nextSteps.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-[rgba(55,53,47,0.06)] dark:border-white/6">
                        <span className="text-[10px] font-semibold text-[#6F6E69] dark:text-white/60 uppercase tracking-wider">Cosa fare adesso</span>
                        <ul className="mt-1 space-y-1">
                          {chatSummary.nextSteps.map((step, i) => (
                            <li key={i} className="text-[10px] text-[#37352F] dark:text-white/70 flex items-start gap-1.5">
                              <span className="text-[#6F6E69] mt-[3px] text-[10px]">▸</span>
                              {step}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {chatSummary.requestsCount > 0 && (
                      <div className="mt-2 text-[10px] text-[#6F6E69] dark:text-white/60">
                        Il cliente ha chiesto {chatSummary.requestsCount === 1 ? '1 cosa' : `${chatSummary.requestsCount} cose`}: le trovi qui sotto
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

        {linkedClientId ? (
          /* ===== MODALITÀ CON CLIENTE ===== */
          <>
            <ClientCrmSections
              data={crm}
              onOpenNotes={onOpenNotes}
              notesCount={chatNotesCount}
              onInsertText={handleInsertComposedMessage}
              textStyle="whatsapp"
              whatsappChatId={chatId}
              activitySuggestionCards={activitySuggestions.map(s => renderCard(s, true))}
              projectSuggestionCards={projectSuggestions.map(s => renderCard(s, true))}
            />

            {/* Suggerimenti non categorizzati */}
            {otherSuggestions.length > 0 && (
              <CollapsibleSection title="Altri suggerimenti" icon={Bot} count={otherSuggestions.length} defaultOpen>
                <div className="space-y-1.5">
                  {otherSuggestions.map(s => renderCard(s, true))}
                </div>
              </CollapsibleSection>
            )}
          </>
        ) : (
          /* ===== MODALITÀ SENZA CLIENTE ===== */
          <>
            {chatSuggestionsLoading || isAnalyzing ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <Loader2 className="w-6 h-6 animate-spin text-[#2383E2]" />
                <p className="text-[13px] text-[#6F6E69] dark:text-white/60">{isAnalyzing ? 'Sto leggendo la chat...' : 'Caricamento...'}</p>
              </div>
            ) : chatSuggestions.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <div className="w-10 h-10 rounded-lg bg-[rgba(55,53,47,0.04)] dark:bg-white/[0.04] flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-[#6F6E69]" />
                </div>
                <p className="text-[13px] font-medium text-[#37352F] dark:text-white">Nessun suggerimento</p>
                <p className="text-[11px] text-[#6F6E69] mt-0.5 max-w-[220px] text-center leading-relaxed">
                  Premi "Aggiorna i suggerimenti" per far leggere all'assistente gli ultimi messaggi
                </p>
              </div>
            ) : (
              <div className="p-3.5">
                {/* Scope filter tabs — Notion style */}
                {(outScopeCount > 0 || alertCount > 0) && (
                  <div className="flex items-center gap-0.5 mb-3 border-b border-[rgba(55,53,47,0.06)] dark:border-white/6">
                    {(['all', 'in_scope', 'alerts', 'out_scope'] as const)
                      .filter(f => f === 'all' || f === 'in_scope' || (f === 'out_scope' && outScopeCount > 0) || (f === 'alerts' && alertCount > 0))
                      .map(f => {
                        const isActive = scopeFilter === f;
                        const dotColor = f === 'alerts' ? '#EB5757' : f === 'out_scope' ? '#F2994A' : undefined;
                        return (
                          <button key={f} onClick={() => setScopeFilter(f)}
                            {...tip(SCOPE_TAB_TIPS[f])}
                            className={`relative px-2.5 py-1.5 text-[11px] font-medium transition-colors ${
                              isActive
                                ? 'text-[#37352F] dark:text-white'
                                : 'text-[#6F6E69] hover:text-[#37352F] dark:hover:text-white'
                            }`}>
                            <span className="flex items-center gap-1">
                              {dotColor && <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: dotColor }} />}
                              {SCOPE_TAB_LABELS[f]}
                              <span className={`text-[10px] tabular-nums ${isActive ? 'text-[#37352F]/60 dark:text-white/60' : 'text-[#6F6E69]'}`}>
                                {f === 'all' ? chatSuggestions.length : f === 'in_scope' ? inScopeCount : f === 'alerts' ? alertCount : outScopeCount}
                              </span>
                            </span>
                            {isActive && <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[calc(100%-16px)] h-[2px] bg-[#37352F] dark:bg-white rounded-full" />}
                          </button>
                        );
                      })}
                  </div>
                )}

                {/* Toolbar */}
                <div className="flex items-center justify-between mb-3">
                  <button onClick={handleSelectAll} className="flex items-center gap-1.5 text-[11px] text-[#6F6E69] hover:text-[#37352F] dark:hover:text-white transition-colors">
                    {allSelected ? <CheckSquare className="w-3.5 h-3.5 text-[#2383E2]" /> : <Square className="w-3.5 h-3.5" />}
                    {allSelected ? 'Deseleziona tutti' : 'Seleziona tutti'}
                  </button>
                  <span className="text-[11px] text-[#6F6E69]">{filteredSuggestions.length} suggeriment{filteredSuggestions.length === 1 ? 'o' : 'i'}</span>
                </div>

                {/* Suggestions list */}
                <div className="space-y-2.5">
                  {filteredSuggestions.map(s => renderCard(s))}
                </div>
              </div>
            )}
          </>
        )}
          </div>

          {/* === AI CHAT — fisso in fondo === */}
          <AskAiSection source={askSource} />

          {/* === BULK ACTIONS FOOTER === */}
          {selectedIds.size > 0 && (
            <div className="px-3.5 py-2.5 border-t border-[rgba(55,53,47,0.06)] dark:border-white/6 flex-shrink-0">
              {!linkedClientId && !hasProject && (
                <p className="text-[11px] text-amber-800 dark:text-amber-300 mb-2">
                  Collega la chat a un Lead o Cliente ("Collega Lead/Cliente" in alto) per creare Task commerciali
                </p>
              )}
              <div className="flex items-center gap-2">
                {linkedClientId && (
                  <button onClick={handleBulkSalesTasks} disabled={bulkSalesSaving}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-1.5 text-[12px] font-medium text-white bg-[#2383E2] hover:bg-[#1a6fc2] disabled:opacity-50 rounded-md transition-colors"
                    {...tip('Un Task commerciale per ogni suggerimento, sulla scheda del Lead/Cliente, con scadenza il prossimo giorno lavorativo alle 9')}>
                    {bulkSalesSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ListTodo className="w-3.5 h-3.5" />}
                    {selectedIds.size === 1 ? 'Crea Task commerciale' : `Crea ${selectedIds.size} Task commerciali`}
                  </button>
                )}
                {hasProject && (
                  <button onClick={handleOpenBulkModal}
                    className={`${linkedClientId ? '' : 'flex-1 justify-center text-white bg-[#2383E2] hover:bg-[#1a6fc2] '}flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] font-medium rounded-md transition-colors ${linkedClientId ? 'text-[#37352F] dark:text-white border border-[rgba(55,53,47,0.16)] dark:border-white/15 hover:bg-[rgba(55,53,47,0.06)]' : ''}`}
                    {...tip(`Crea task nel progetto ${projectName || 'collegato'}`)}>
                    <FolderKanban className="w-3.5 h-3.5" /> {linkedClientId ? 'Nel progetto' : `Crea ${selectedIds.size} task nel progetto`}
                  </button>
                )}
                {selectedIds.size >= 2 && (
                  <button onClick={handleGroupSelected} className="p-1.5 text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/15 rounded-md transition-colors" {...iconTip('Unisci in una sola richiesta')}>
                    <Group className="w-3.5 h-3.5" />
                  </button>
                )}
                <button onClick={handleBulkDismiss} className="p-1.5 text-[#6F6E69] hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/15 rounded-md transition-colors" {...iconTip('Scarta i suggerimenti selezionati')}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}

          {/* === MODALS === */}
          <BulkTaskConfirmModal isOpen={showBulkModal} onClose={() => setShowBulkModal(false)}
            suggestions={chatSuggestions.filter(s => selectedIds.has(s.id))} projectName={projectName || 'Progetto'} chatId={chatId} onConfirm={handleBulkConfirm} />
        </>
      )}
    </div>
  );
}
